import { PencilIcon, PhotoIcon, TrashIcon } from '@heroicons/react/24/solid';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { twMerge } from 'tailwind-merge';

import Badge, { BadgeVariant } from 'components/Badge/Badge';
import CTAModal from 'components/CTAModal/CTAModal';
import ImageUploadButton from 'components/ImageUploader/ImageUploadButton';
import { t } from 'locales/i18n';
import LabelWithTooltip from 'pages/Overview/LabelWithTooltip';

interface IImageUploaderProps {
  image?: string;
  onDelete: () => Promise<void>;
  onEdit: (asset: Blob) => void;
  labelText: string;
  tooltipText: string;
  containerClassName?: string;
  useImageAsBackground?: boolean;
  id: string;
  isDefaultBranding?: boolean;
  useDarkContrast?: boolean;
  sectionDataTestId?: string;
}

const ImageUploader = ({
  image,
  onDelete,
  onEdit,
  labelText,
  tooltipText,
  containerClassName,
  useImageAsBackground,
  id,
  isDefaultBranding,
  useDarkContrast,
  sectionDataTestId,
}: IImageUploaderProps) => {
  const [mouseHover, setMouseHover] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [imageSource, setImageSource] = useState('');

  const handleMouseEnter = useCallback(() => {
    setMouseHover(true);
  }, []);

  const handleMouseLeave = useCallback(() => {
    setMouseHover(false);
  }, []);

  useEffect(() => {
    if (!image) {
      setImageSource('');
      return;
    }

    setImageSource(image);
  }, [image]);

  const imageUploadContainerClassName = twMerge(
    'h-52',
    'overflow-hidden',
    'rounded-2xl',
    'border',
    'border-slate-300',
    !!image && useDarkContrast ? 'bg-kira-slate-700' : 'bg-kira-slate-100',
    'bg-cover',
    'bg-center',
    'bg-no-repeat',
    'relative',
    'transition-all',
    'ease-in-out',
    'delay-50',
    'duration-200',
    'hover:ring-kira-blue-800',
    'hover:ring-4',
    containerClassName
  );

  const photoIconClassName = twMerge(
    'absolute',
    'top-1/2',
    'left-1/2',
    'h-16',
    'w-16',
    '-translate-x-1/2',
    '-translate-y-1/2',
    'text-kira-slate-400'
  );

  const handleOpenModal = useCallback(() => {
    setIsModalOpen(true);
  }, []);

  const handleDeleteImage = () => {
    onDelete();
    setImageSource('');
    setIsModalOpen(false);
  };

  const handleUpload = () => {
    const fileInput = document.getElementById(id) as HTMLInputElement;
    fileInput.click();
    setIsModalOpen(false);
  };

  const handleFileUploadChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (event.target?.files && event.target.files[0] instanceof Blob) {
      onEdit(event.target.files[0]);
      const reader = new FileReader();
      reader.readAsDataURL(event.target.files[0]);
      reader.onload = (e: ProgressEvent<FileReader>) => {
        const result = e.target?.result;
        if (typeof result === 'string') {
          setImageSource(result);
        }
      };
    }
  };

  const hoverButton = useCallback(
    () =>
      imageSource && imageSource !== '' ? (
        <ImageUploadButton
          icon={<TrashIcon />}
          onClick={handleOpenModal}
          dataTestId="delete"
        />
      ) : (
        <ImageUploadButton
          icon={<PencilIcon />}
          styles="hover:bg-kira-blue-300 hover:text-white"
          onClick={handleUpload}
          dataTestId="delete"
        />
      ),
    [imageSource, image, handleOpenModal, handleUpload, isDefaultBranding]
  );

  const backgroundImageStyle = useMemo(
    () =>
      useImageAsBackground ? { backgroundImage: `url(${imageSource})` } : {},
    [imageSource, useImageAsBackground]
  );

  const CTAaction = useMemo(() => {
    if (isDefaultBranding && imageSource) {
      return handleDeleteImage;
    }
    if (isDefaultBranding && !imageSource) {
      return handleUpload;
    }
    return handleDeleteImage;
  }, [imageSource, isDefaultBranding, handleDeleteImage, handleUpload]);

  return (
    <>
      <div
        className="relative flex-1 space-y-2"
        data-testid={sectionDataTestId}
      >
        <LabelWithTooltip label={labelText} tooltipText={tooltipText} />
        <div
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          className={imageUploadContainerClassName}
          style={backgroundImageStyle}
        >
          {isDefaultBranding && !mouseHover && (
            <div className="absolute right-2 top-2">
              <Badge variant={BadgeVariant.PURPLE} text={t('Default')} />
            </div>
          )}
          {!imageSource ? <PhotoIcon className={photoIconClassName} /> : null}
          {mouseHover ? hoverButton() : null}

          {!useImageAsBackground && imageSource && (
            <img src={imageSource} alt={labelText} className="w-[70%]" />
          )}
        </div>
      </div>
      <CTAModal
        isOpen={isModalOpen}
        setIsOpen={setIsModalOpen}
        title={
          isDefaultBranding ? t('Remove Default Branding') : t('Delete Image')
        }
        description={
          isDefaultBranding
            ? t(
                'Deleting a default image will remove all default branding for this assessment. Once removed, you will be able to upload new custom branding images.'
              )
            : t("Deleting an image is permanent and can't be undone.")
        }
        confirmation={
          isDefaultBranding
            ? t('Are you sure you want to remove the default branding?')
            : t('Are you sure you want to delete this image?')
        }
        action={CTAaction}
        primaryButtonText={isDefaultBranding ? t('Remove') : t('Delete')}
        buttonDataTestId={isDefaultBranding ? 'remove' : 'delete'}
      />
      <input
        type="file"
        accept=".png, .jpg, .jpeg"
        id={id}
        name="file-uploader"
        className="hidden"
        disabled={false}
        onChange={handleFileUploadChange}
      />
    </>
  );
};

export default ImageUploader;
